import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { legalPages } from '../data/mock';
import SEO from '../components/SEO';

const legalPageMap = {
  '/privacy': 'privacy',
  '/terms': 'terms',
  '/cookies': 'cookies',
  '/accessibility': 'accessibility',
  '/disclaimer': 'disclaimer'
};

const seoData = {
  privacy: {
    title: 'Privacy Policy',
    description: 'CORE IT Group Privacy Policy - Learn how we collect, use, and protect your personal information.',
    canonical: '/privacy'
  },
  terms: {
    title: 'Terms of Use',
    description: 'CORE IT Group Terms of Use - Terms and conditions for using our website and services.',
    canonical: '/terms'
  },
  cookies: {
    title: 'Cookie Policy',
    description: 'CORE IT Group Cookie Policy - Information about how we use cookies on our website.',
    canonical: '/cookies'
  },
  accessibility: {
    title: 'Accessibility Statement',
    description: 'CORE IT Group Accessibility Statement - Our commitment to digital accessibility for all users.',
    canonical: '/accessibility'
  },
  disclaimer: {
    title: 'Disclaimer',
    description: 'CORE IT Group Disclaimer - Important information about the content on our website.',
    canonical: '/disclaimer'
  }
};

const LegalPage = () => {
  const location = useLocation();
  const pageKey = legalPageMap[location.pathname];
  const pageData = legalPages[pageKey];
  const seo = seoData[pageKey];

  if (!pageData) {
    return (
      <div className="bg-slate-950 pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Page Not Found</h1>
          <Link to="/">
            <Button className="bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-950 pt-20">
      <SEO 
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
      />
      {/* Hero Section */}
      <section className="relative py-24">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/2 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-8">
            {pageData.title}
          </h1>

          <div className="prose prose-invert prose-slate max-w-none">
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 sm:p-12">
              {pageData.content.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-slate-400 leading-relaxed mb-4 last:mb-0">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>

          <p className="text-slate-500 text-sm mt-8">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </p>
        </div>
      </section>
    </div>
  );
};

export default LegalPage;
